import { useCallback, useEffect, useRef } from 'react';

// Type declarations for Web Speech API
interface SpeechRecognition extends EventTarget {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  maxAlternatives: number;
  start: () => void;
  stop: () => void;
  onresult: ((event: SpeechRecognitionEvent) => void) | null;
  onerror: ((event: SpeechRecognitionErrorEvent) => void) | null;
  onend: (() => void) | null;
}

interface SpeechRecognitionEvent extends Event {
  results: SpeechRecognitionResultList;
}

interface SpeechRecognitionResultList {
  length: number;
  item(index: number): SpeechRecognitionResult;
  [index: number]: SpeechRecognitionResult;
}

interface SpeechRecognitionResult {
  isFinal: boolean;
  length: number;
  item(index: number): SpeechRecognitionAlternative;
  [index: number]: SpeechRecognitionAlternative;
}

interface SpeechRecognitionAlternative {
  transcript: string;
  confidence: number;
}

interface SpeechRecognitionErrorEvent extends Event {
  error: string;
  message: string;
}

interface UseWebSpeechOptions {
  onResult: (transcript: string) => void;
  onError?: (error: string) => void;
  lang?: string;
}

interface UseWebSpeechReturn {
  isSupported: boolean;
  start: () => void;
  stop: () => void;
}

export const useWebSpeech = ({ onResult, onError, lang = 'en-GB' }: UseWebSpeechOptions): UseWebSpeechReturn => {
  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const isListeningRef = useRef(false);

  // Check browser compatibility
  const isSupported =
    typeof window !== 'undefined' && ('SpeechRecognition' in window || 'webkitSpeechRecognition' in window);

  // Initialize speech recognition
  useEffect(() => {
    if (!isSupported) {
      onError?.('Speech recognition is not supported in this browser');
      return;
    }

    // Create recognition instance
    const SpeechRecognitionConstructor = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;

    const recognition = new SpeechRecognitionConstructor() as SpeechRecognition;

    // Configure recognition
    recognition.lang = lang;
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.maxAlternatives = 1;

    // Handle results
    recognition.onresult = (event: SpeechRecognitionEvent) => {
      const results = event.results;
      const lastResult = results[results.length - 1];

      // Only process final results to avoid duplicates
      if (lastResult.isFinal) {
        const transcript = lastResult[0].transcript;
        if (transcript.trim()) {
          onResult(transcript);
        }
      }
    };

    // Handle errors
    recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
      // Ignore no-speech errors as they're common during pauses
      if (event.error !== 'no-speech') {
        onError?.(event.error);
      }

      isListeningRef.current = false;
    };

    // Handle end event and restart if needed
    recognition.onend = () => {
      // Auto-restart if still supposed to be listening
      if (isListeningRef.current) {
        try {
          recognition.start();
        } catch (_e) {
          isListeningRef.current = false;
        }
      }
    };

    recognitionRef.current = recognition;

    return () => {
      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop();
        } catch (_e) {
          // Ignore errors on cleanup
        }
      }
    };
  }, [isSupported, lang, onResult, onError]);

  const start = useCallback(() => {
    if (!recognitionRef.current || isListeningRef.current) {
      return;
    }

    try {
      isListeningRef.current = true;
      recognitionRef.current.start();
    } catch (_e) {
      isListeningRef.current = false;
      onError?.('Failed to start speech recognition');
    }
  }, [onError]);

  const stop = useCallback(() => {
    if (!recognitionRef.current || !isListeningRef.current) return;

    try {
      isListeningRef.current = false;
      recognitionRef.current.stop();
    } catch (_e) {
      // Silently handle stop errors
    }
  }, []);

  return {
    isSupported,
    start,
    stop,
  };
};
